import { styled } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  isRemindable,
  type PendingProposalReviewer,
  pendingProposalReviewers,
  type StagedProposalReviewerChanges,
} from "work/proposals/_views/_shared/proposal-pending-review-warning";
import {
  type ProposalReviewerReminderState,
  useRemindProposalReviewers,
} from "work/proposals/reviewers/_hooks/use-remind-proposal-reviewers";

/** How many pending reviewers show before the rest fold into "+N more". */
export const PENDING_REVIEWERS_SHOWN = 3;


const List = styled("ul")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  listStyle: "none",
  margin: 0,
  padding: 0,
}));
const Row = styled("li")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));
const Who = styled("div")(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minWidth: 0,
}));
const Name = styled(Typography)(() => ({
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const Footer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
}));

type PendingReviewerRemindersProps = {
  envelope: ProposalCollaborationEnvelope;
  viewerScope?: string;
  /** The leader's unsaved roster edits. A reviewer staged for removal is not listed (#6869). */
  stagedReviewerChanges?: StagedProposalReviewerChanges;
  className?: string;
};

/**
 * Who has not reviewed what is about to be sent, and a way to remind each of them (#6618).
 *
 * The submit dialog used to say only "2 reviewers have not reviewed your latest changes", which left
 * the leader to go and find out who. This names them, says whether each never reviewed or approved
 * an earlier version, and offers Remind on each row, and Remind all when there are several. It
 * draws the same list in the Reviewers popover, so a reminder does not have to begin with
 * submitting. It draws nothing when nobody is pending.
 */
export default function PendingReviewerReminders(props: Readonly<PendingReviewerRemindersProps>) {
  const { envelope, viewerScope, stagedReviewerChanges, className } = props;
  const pending = pendingProposalReviewers(envelope, stagedReviewerChanges);
  const reminders = useRemindProposalReviewers({ envelope, viewerScope });
  const [expanded, setExpanded] = useState(false);
  const userIds = pending.map((reviewer) => reviewer.userId);
  const individualsQuery = useGetIndividualsByUserIdsQuery(userIds.length ? { userIds } : skipToken);
  const individuals = Individual.fromJSONArray(individualsQuery.data, { allowEmpty: true }) ?? [];

  if (pending.length === 0) return null;

  // The directory names every reviewer it is asked about, fetching any it has not cached.
  function individualOf(userId: string): Individual | undefined {
    return individuals.find((item) => item.userId?.value === userId);
  }

  function nameOf(userId: string): string {
    return individualOf(userId)?.fullName ?? "Unknown reviewer";
  }

  const shown = expanded ? pending : pending.slice(0, PENDING_REVIEWERS_SHOWN);
  const hiddenCount = pending.length - shown.length;
  const remindableKind = pending.filter(isRemindable);
  const remindable = remindableKind.filter((reviewer) => reminders.stateOf(reviewer.userId).status === "idle");

  return (
    <div className={className} data-testid="pending-reviewer-reminders">
      <List aria-label="Reviewers who have not reviewed">
        {shown.map((reviewer) => (
          <PendingReviewerRow
            key={reviewer.userId}
            reviewer={reviewer}
            name={nameOf(reviewer.userId)}
            individual={individualOf(reviewer.userId)}
            canRemind={reminders.canRemind && isRemindable(reviewer)}
            state={reminders.stateOf(reviewer.userId)}
            onRemind={() => reminders.remind(reviewer)}
          />
        ))}
      </List>
      {(hiddenCount > 0 || (reminders.canRemind && remindableKind.length > 1)) && (
        <Footer>
          {hiddenCount > 0 ? (
            <Button size="small" onClick={() => setExpanded(true)}>
              {`+${hiddenCount} more`}
            </Button>
          ) : (
            <span />
          )}
          {reminders.canRemind && remindableKind.length > 1 && (
            <Button size="small" disabled={remindable.length === 0} onClick={() => reminders.remindAll(remindable)}>
              Remind all
            </Button>
          )}
        </Footer>
      )}
    </div>
  );
}

function PendingReviewerRow(
  props: Readonly<{
    reviewer: PendingProposalReviewer;
    name: string;
    individual?: Individual;
    canRemind: boolean;
    state: ProposalReviewerReminderState;
    onRemind: () => void;
  }>,
) {
  const { reviewer, name, individual, canRemind, state, onRemind } = props;
  const status = PENDING_STATUS_TEXT[reviewer.status];
  return (
    <Row>
      <IndividualAvatar individual={individual} />
      <Who>
        <Name variant="body2">{name}</Name>
        <Typography variant="caption" color="text.secondary">
          {status}
        </Typography>
        {state.status === "refused" && (
          <Typography variant="caption" color="error" role="alert">
            {state.message}
          </Typography>
        )}
      </Who>
      {canRemind && (
        <Button
          size="small"
          variant="outlined"
          aria-label={state.status === "sent" ? `Reminded ${name} just now` : `Remind ${name}`}
          disabled={state.status !== "idle" && state.status !== "refused"}
          onClick={onRemind}
        >
          {reminderButtonText(state)}
        </Button>
      )}
    </Row>
  );
}

const PENDING_STATUS_TEXT: Record<PendingProposalReviewer["status"], string> = {
  notReviewed: "Not reviewed yet",
  approvedEarlierVersion: "Approved an earlier version",
  // They wrote the latest change, so they have seen it; Remind asks them to finalize (#6868).
  notFinalized: "Made changes · hasn't finalized",
  // Not reminded: the leader has not sent it to them, and Send is what tells them (review of #6781).
  notSentYet: "Not sent yet · use Send to ask them",
};

function reminderButtonText(state: ProposalReviewerReminderState): string {
  switch (state.status) {
    case "sending":
      return "Reminding…";
    case "sent":
      return "Reminded just now";
    default:
      return "Remind";
  }
}
